import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// WordPress REST API URL
const WORDPRESS_API_URL = 'https://cms.nausurology.org/wp-json/wp/v2';

const Training = () => {
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrainingPrograms = async () => {
      try {
        // We use _embed to get featured images and author data in one request
        const response = await fetch(`${WORDPRESS_API_URL}/training_program?_embed`);

        if (!response.ok) {
          if (response.status === 404) {
            // Custom post type doesn't exist yet
            setTrainingPrograms([]);
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        // Ensure data is an array
        setTrainingPrograms(Array.isArray(data) ? data : []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching training programs:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchTrainingPrograms();
  }, []);

  const handleProgramClick = (programId) => {
    navigate(`/training/${programId}`);
  };

  if (loading) {
    return (
      <div className="section" style={{ textAlign: 'center', padding: '5rem 0' }}>
        <div className="container">
          <div className="loader" style={{ marginBottom: '1rem' }}>⌛</div>
          <h1>Loading Training Programs...</h1>
          <p>Please wait while we fetch the latest information from NAUS.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section" style={{ textAlign: 'center', padding: '5rem 0' }}>
        <div className="container" style={{ color: '#721c24', backgroundColor: '#f8d7da', padding: '2rem', borderRadius: '8px' }}>
          <h1>Connection Error</h1>
          <p>We're having trouble connecting to the training database.</p>
          <p><small>Technical details: {error}</small></p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn btn-orange" 
            style={{ marginTop: '1rem' }}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="section" style={{ padding: '4rem 0' }}>
      <div className="container">
        <h1 className="section-title" style={{ textAlign: 'center', color: '#006400' }}>Training Programs</h1>
        <p style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '3rem', maxWidth: '700px', margin: '0 auto 3rem' }}>
          Explore our comprehensive training programs designed to advance your skills in urological surgery.
        </p>

        {trainingPrograms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', backgroundColor: '#f9f9f9', borderRadius: '12px' }}>
            <h2>No programs found.</h2>
            <p>There are currently no active training programs listed. Please check back later.</p>
          </div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
            gap: '2.5rem' 
          }}>
            {trainingPrograms.map((program) => {
              // Extracting the featured image via WP REST API _embed
              const featuredMedia = program._embedded?.['wp:featuredmedia']?.[0];
              const imageUrl = featuredMedia?.source_url || 'https://via.placeholder.com/400x250?text=NAUS+Training';

              const formattedDate = new Date(program.date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });

              return (
                <div 
                  key={program.id} 
                  className="training-card"
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    transition: 'transform 0.3s ease'
                  }}
                  onClick={() => handleProgramClick(program.id)}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <div style={{ position: 'relative', height: '220px' }}>
                    <img
                      src={imageUrl}
                      alt={program.title?.rendered || 'Training Program'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <div style={{ 
                      position: 'absolute', 
                      top: '10px', 
                      right: '10px', 
                      backgroundColor: 'rgba(0,100,0,0.85)', 
                      color: 'white', 
                      padding: '4px 12px', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem' 
                    }}>
                      New
                    </div>
                  </div>

                  <div style={{ padding: '1.5rem' }}>
                    <span style={{ color: '#888', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>
                      Published: {formattedDate}
                    </span>
                    
                    <h3
                      style={{
                        margin: '0 0 1rem 0',
                        fontSize: '1.4rem',
                        color: '#006400',
                        lineHeight: '1.3'
                      }}
                      dangerouslySetInnerHTML={{ __html: program.title?.rendered || 'Untitled Program' }}
                    />

                    <div
                      style={{
                        color: '#555',
                        lineHeight: '1.6',
                        fontSize: '0.95rem',
                        marginBottom: '1.5rem',
                        height: '4.8em',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}
                      dangerouslySetInnerHTML={{ __html: program.excerpt?.rendered || 'No description available.' }}
                    />
                    
                    <button 
                      className="btn"
                      style={{ 
                        width: '100%',
                        padding: '0.8rem',
                        backgroundColor: '#008080',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      View Program Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Training;